package pl.decks.personal.components.widgets.timeline

data class YearMonth(val year: Int, val month: Month) : Comparable<YearMonth> {
    override fun compareTo(other: YearMonth): Int =
        compareValuesBy(this, other, { it.year }, { it.month.ordinal })

    operator fun plus(months: Int): YearMonth {
        val totalMonths = (month.ordinal + months)
        return YearMonth(
            year = year + totalMonths / 12,
            month = Month(totalMonths % 12 + 1),
        )
    }

    operator fun minus(other: YearMonth): Int =
        (this.year - other.year) * 12 + this.month.numbered - other.month.numbered

    operator fun minus(months: Int): YearMonth {
        val totalMonths = month.ordinal - months
        val yearOffset = if (totalMonths >= 0) totalMonths / 12 else (totalMonths - 11) / 12
        val monthIndex = ((totalMonths % 12) + 12) % 12
        return YearMonth(
            year = year + yearOffset,
            month = Month(monthIndex + 1),
        )
    }

    data class Range(
        val from: YearMonth,
        val to: YearMonth,
    ) : Iterable<YearMonth>, ClosedRange<YearMonth> {
        override val start = from
        override val endInclusive: YearMonth = to
        val span = to - from + 1

        init {
            require(from <= to) { "to ($to) must be >= from: $from" }
        }

        operator fun contains(other: Range): Boolean =
            other.from >= this.from && other.to <= this.to

        override fun iterator(): Iterator<YearMonth> = iterator {
            var cur = from
            while (cur <= to) {
                yield(cur); cur += 1
            }
        }

        fun splitByYear(): List<Range> = (from.year..to.year).map { y ->
            Range(
                from = if (y == from.year) from else YearMonth(y, Month.JANUARY),
                to = if (y == to.year) to else YearMonth(y, Month.DECEMBER),
            )
        }

        fun intersection(other: Range): Range? {
            val from = maxOf(this.from, other.from)
            val to = minOf(this.to, other.to)
            return if (from <= to) Range(from, to) else null
        }
    }
}
